import { VideoPageheader } from '@shared/Frontend/components/pageheader'
import * as i18next from 'i18next'
import { List } from 'immutable'
import * as React from 'react'
import { fromJSX, Fun, Widget } from 'widgets-for-react'
import { link, linkWrapper } from '../../cms/blocks/utils.gen'
import { parseImageUrl } from '../../drupal/rendering/image_utils.mod'
import { Breadcrumb, CustomParsedNode } from '../../drupal/types_and_parser/extra_types.mod'
import {
  ParsedMediaImage,
  ParsedNodeCareer,
  ParsedNodeCase,
  ParsedNodeContact,
  ParsedNodeExpertise,
  ParsedNodePage,
  ParsedNodeProject,
  ParsedNodeUpdate,
  ParsedNodeUpdateOverview
} from '../../drupal/types_and_parser/types.ext.gen'
import { InnerHtmlWrapper } from '../../utils/InnerHtmlWrapper'

export const pageheaderWidget: Fun<
  { breadcrumbs: List<Breadcrumb>; node: CustomParsedNode; viewportWidth: number },
  Widget<never>
> = ({ breadcrumbs, node, viewportWidth }) => {
  switch (node.bundle) {
    case 'homepage':
      return fromJSX(() => (
        <VideoPageheader
          title={node.field_introduction}
          poster={
            node.field_introduction_image
              ? {
                  alt: '',
                  src: parseImageUrl(node.field_introduction_image.field_media_image.image.style_urls['1920w']),
                  crops: []
                }
              : undefined
          }
          kpis={node.field_icons
            .map((icon) => ({
              icon: icon.field_icon,
              text: icon.field_title
            }))
            .toArray()}
          primaryUpdater={
            node.field_button
              ? { ...linkWrapper(node.field_button), isExternal: linkWrapper(node.field_button).is_link_external }
              : undefined
          }
          viewportWidth={viewportWidth}
        />
      ))
    case 'project':
      return ProjectPageHeader(breadcrumbs)(node)
    case 'expertise':
      return ExpertisePageHeader(breadcrumbs)(node)
    case 'update':
      return UpdatePageHeader(breadcrumbs)(node)
    case 'update_overview':
      return UpdateOverviewPageHeader(breadcrumbs)(node)
    case 'career':
      return CareersPageHeader(breadcrumbs)(node)
    case 'page':
      return PageHeader(breadcrumbs)(node)
    case 'contact':
      return ContactPageHeader(breadcrumbs)(node)
    case 'case':
      return CasePageHeader(breadcrumbs)(node)
    default:
      return DefaultPageHeader(breadcrumbs)(node)
  }
}

const DefaultPageHeader: Fun<List<Breadcrumb>, Fun<CustomParsedNode, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:career'),
    header: 'generic',
    title: '', // TODO: Page content type does not have field_introduction
    hasHeroLabel: false,
    hasTop: false,
    links: [], // TODO: Page content type does not have field_links or field_button
    image: p.field_introduction_image
  })

const PageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodePage, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:career'),
    header: 'generic',
    title: p.field_introduction, // TODO: Page content type does not have field_introduction
    hasHeroLabel: false,
    hasTop: false,
    links: p.field_links.toArray(), // TODO: Page content type does not have field_links or field_button
    image: p.field_introduction_image
  })

const ContactPageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeContact, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:contact'),
    header: 'generic',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: p.field_links.toArray(),
    image: p.field_introduction_image
  })

const ProjectPageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeProject, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:project'),
    header: 'project',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: [],
    info: {
      kind: 'list',
      items: p.field_icons
        .map((icon) => ({
          icon: icon.field_icon,
          text: icon.field_title
        }))
        .toArray()
    },
    image: p.field_introduction_image
  })

const UpdatePageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeUpdate, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:updates'),
    header: 'detail',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: [],
    image: p.field_introduction_image,
    content_tags: p.field_category.map((x) => x.label).toArray()
  })

const UpdateOverviewPageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeUpdateOverview, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:updates'),
    header: 'generic',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: p.field_links.toArray(),
    image: p.field_introduction_image
  })

const ExpertisePageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeExpertise, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:expertise'),
    header: 'expertise',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: p.field_links.toArray(),
    info: {
      kind: 'icon',
      icon: p.field_icon.field_icon
    },
    image: p.field_introduction_image
  })

const CareersPageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeCareer, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:careers'),
    header: 'generic',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: p.field_links.toArray(),
    button: p.field_button ? [p.field_button] : [],
    image: p.field_introduction_image
  })

const CasePageHeader: Fun<List<Breadcrumb>, Fun<ParsedNodeCase, Widget<never>>> = (b) => (p) =>
  GenericPageHeader(b)({
    tag: i18next.t('Header:case'),
    header: 'expertise',
    title: p.field_introduction,
    hasHeroLabel: true,
    hasTop: false,
    links: p.field_links.toArray(),
    info: {
      kind: 'icon',
      icon: p.field_icon.field_icon
    },
    image: p.field_introduction_image
  })

const applyIf =
  <a,>(b: boolean, f: (_: a) => a) =>
  (a: a): a =>
    b ? f(a) : a
const wrapIf = (b: boolean, f: (_: JSX.Element) => JSX.Element) => applyIf(b, f)

type PageHeaderProps = {
  tag: string
  header: string
  title?: string
  text?: string
  hasTop: boolean
  hasHeroLabel: boolean
  links?: { title: string; url: string }[]
  button?: { title: string; url: string }[]
  info?:
    | {
        kind: 'icon'
        icon: string
      }
    | {
        kind: 'list'
        items: {
          icon: string
          text: string
        }[]
      }
  image?: ParsedMediaImage
  content_tags?: string[]
}

export const GenericPageHeader: Fun<List<Breadcrumb>, Fun<PageHeaderProps, Widget<never>>> = () => (props) =>
  fromJSX(() => (
    <header className={`hero hero--${props.header}${props.tag == 'careers' ? ' hero--careers' : ''}`}>
      <div className="hero__inner ls-container">
        {wrapIf(props.hasTop, (jsx) => <div className="hero__top">{jsx}</div>)(
          <>
            <div className="hero__content">
              <div className="hero__header">
                {props.hasHeroLabel && <span className="hero__label">{props.tag}</span>}

                {props.title && <h1 className="hero__title title--m">{props.title}</h1>}
                {props.text && (
                  //<p dangerouslySetInnerHTML={{ __html: props.text }}></p>
                  <InnerHtmlWrapper text={props.text} />
                )}
              </div>
              {props.content_tags && props.content_tags.length > 0 && (
                <ul className="hero__list tags">
                  {props.content_tags.map((tag) => (
                    <li className="tags__item">
                      <a className="tag tag--m">{tag}</a>
                    </li>
                  ))}
                </ul>
              )}
              {props.links && props.links.length > 0 && (
                <div className="hero__footer hero__footer--top ">
                  {props.links.map((l) => (
                    <div className="hero__clickable">{link(linkWrapper(l), { classname: 'link' }, l.title)}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="hero__visual">
              {props.tag == 'vacancy' || props.tag == 'vacature' ? (
                <img
                  loading="eager"
                  fetchpriority="high"
                  className="pageheader__image"
                  alt={props.image.field_media_image.alt}
                  width="2880"
                  height="1620"
                  src={props.image.field_media_image.image.style_urls['2880w']}
                  sizes="100vw"
                  srcSet={[
                    ['640w', props.image.field_media_image.image.style_urls['640w']],
                    ['1280w', props.image.field_media_image.image.style_urls['1280w']],
                    ['1920w', props.image.field_media_image.image.style_urls['1920w']],
                    ['2880w', props.image.field_media_image.image.style_urls['2880w']]
                  ].reduce((acc, [s, u]) => {
                    const part = `${u} ${s}`
                    return acc == '' ? part : `${acc}, ${part}`
                  }, '')}
                />
              ) : (
                headerImage(props.image)
              )}
            </div>
          </>
        )}
        {props.button && props.button.length > 0 && props.tag == 'careers' && (
          <div className="hero__footer hero__footer--bottom">
            {props.button.map((l) => (
              <div className="hero__clickable">{link(linkWrapper(l), { classname: 'link' }, l.title)}</div>
            ))}
          </div>
        )}

        {props.info && props.info.kind == 'icon' ? (
          <div className="hero__icon">
            <img
              loading="lazy"
              className="icon__icon"
              alt="icon"
              src={`/frontend/images/icons/project-header/${props.info.icon}`}
              width="70"
              height="70"
            />
          </div>
        ) : (
          <></>
        )}
      </div>

      {props.info && props.info.kind == 'list' ? (
        <div className="hero__icon-wrapper">
          <div className="hero__icon ls-container">
            <ul className="hero__list icons">
              {props.info.items.map((info) => (
                <li className="icons__column icon">
                  <img
                    loading="lazy"
                    className="icon__icon"
                    alt="icon"
                    src={`/frontend/images/icons/project-header/${info.icon}`}
                    width="70"
                    height="70"
                  />
                  <p className="icon__text text--s">{info.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <></>
      )}
    </header>
  ))

const headerImage = (image?: ParsedMediaImage) => {
  if (image && image.field_media_image.image) {
    const data_src_url = image.field_media_image.image.style_urls['680x340']
    if (data_src_url) {
      const data_src = parseImageUrl(data_src_url)
      const data_src_set = [
        ['680w', image.field_media_image.image.style_urls['680x340']],
        ['1360w', image.field_media_image.image.style_urls['1360x680']],
        ['2720w', image.field_media_image.image.style_urls['2720x1360']]
      ]
        .filter(([_, u]) => !!u)
        .reduce((acc, [s, u]) => {
          const part = `${parseImageUrl(u)} ${s}`
          return acc == '' ? part : `${acc}, ${part}`
        }, '')
      return (
        <img
          loading="eager"
          fetchpriority="high"
          className="pageheader__image"
          alt={image.field_media_image.alt}
          width="680"
          height="340"
          src={data_src}
          sizes={'(min-width: 1800px) 1360px, (min-width: 1440px) 85vw, (min-width: 1024px) 50vw, 100vw'}
          srcSet={data_src_set}
        />
      )
    }
  }
  return null
}
